import { CPEAPIClient } from '@shared/api';
import { EstimatedAmounts } from '@components/claims/estimated';
import { AdjustAmounts } from '@components/claims/adjust';
import { ExpenseFields } from '@components/claims/expense';
import { Depreciation } from '@components/claims/depreciation';
import { DepreciationCalculation } from '@components/claims/depreciationCalculation';
import {
  LetterTemplate,
  LetterPrefill,
  SentLetter,
  EmailSendConfirmation,
  LetterTemplateList,
} from '@/types/letters';
import { AxiosResponse } from 'axios';
import { BrandsId } from '@/types/common';

export enum CompaniesCPE {
  PREMIER = 'premier',
  OPTIMA = 'optima',
  MULTINATIONAL = 'multinational',
  SODA = 'soda',
}

export const getOrCreateDamageAnalysisCPE = async (
  claimId: string,
  initialClaimReserve?: number,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis'.replace(
    '{claimId}',
    claimId,
  );
  const responseGet = await CPEAPIClient.get(endpoint);

  if (responseGet.data.length > 0) {
    return responseGet.data[0].id;
  } else if (initialClaimReserve === -1) {
    return '';
  }

  const responsePost = await CPEAPIClient.post(endpoint, {
    initialClaimReserve: Number(initialClaimReserve) || 0,
  });

  return responsePost.data[0].id;
};

export const postEstimatesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: EstimatedAmounts,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/estimates'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    expenseAmount: body.expenseAmount,
    date: new Date(),
    costOfNewParts: body.partsCost,
    newPartsDiscountPercentage: body.porcDesc,
    discountAmount: body.discountAmount,
    totalCostOfPartsWithoutDiscount: body.partsCostWithoutDiscount,
    totalCostOfPartsWithDiscount: body.partsCostTotal,
    costOfMaterials: body.materialsCost,
    costOfLabor: body.laborCost,
    taxOnLabor: body.ivuLaborAmount,
    taxOnParts: body.ivuPartsAmount,
    taxPercentage: body.porcIVA,
    subtotalParts: body.subtotalParts,
    totalCostEstimate: body.estimatedTotalAmount,
    previousDamageCost: body.previousDamageCost,
  });
};

export const postAdjusmentsCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: AdjustAmounts,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/adjustments'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  const curUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const agentEmail = curUser?.userName || null;

  await CPEAPIClient.post(endpoint, {
    date: new Date(),
    agentEmail: agentEmail,
    totalCostEstimate: body.estimatedTotalAmount,
    totalToPay: body.totalToPay,
    depreciationPercentage: body.porcDepreciation,
    depreciationAmount: body.depreciationAmount,
    negligencePercentage: body.percNegligence,
    customerResponsabilityAmount: body.customerResponsabilityAmount,
    deductibleAmount: body.deductibleAmount,
    fines: body.fines,
    others: body.others,
    constructive: body.constructive,
    othersPlus: body.othersPlus,
    negligence: body.negligence,
    towCost: body.trailer,
    missingPartsCost: body.missingParts,
    advancedPayment: body.advancePayment,
    totalInsuranceCoverageAmount: body.totalInsuranceAmount,
    closedWithoutPayment: body.closedWithoutPayment,
    isTotalLoss: body.isTotalLoss || false,
  });
};

export const postAdditionalExpensesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: ExpenseFields,
  documentUrl: string,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/additionalExpenses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    date: new Date(),
    expenseType: body.expenseType,
    description: body.expenseDescription,
    amount: body.expenseAmount,
    documentUrl: documentUrl,
  });
};

export const postSubrogationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  insuranceCompany: string,
  croquis: string | null,
  isSubrogate: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/subrogations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    insuranceCompany: insuranceCompany,
    croquis: croquis,
    isSubrogation: isSubrogate,
  });
};

export const updateClaimSubrogationCPE = async (
  claimId: string,
  isSubrogate: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}'.replace('{claimId}', claimId);

  await CPEAPIClient.patch(endpoint, {
    isSubrogation: isSubrogate,
  });
};

export const updateClaimCurrentStageCPE = async (
  claimId: string,
  currentStage: string,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}'.replace('{claimId}', claimId);

  await CPEAPIClient.patch(endpoint, {
    currentStage,
  });
};

/**
 * Updates the vehicle mileage for the claim in CPE (vehicles.mileage).
 * Uses the first vehicle of the first incident. Used when odometer is captured (e.g. inspection form or photo ML).
 */
export const updateClaimVehicleMileageCPE = async (
  claimId: string,
  mileage: number,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/vehicle/mileage'.replace(
    '{claimId}',
    claimId,
  );

  await CPEAPIClient.patch(endpoint, { mileage });
};

export const getTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  const response = await CPEAPIClient.get(endpoint);

  if (response.data.length === 0) {
    return '';
  }

  return response.data[0].id;
};

export const postTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  recoveryAmount: number,
  updatedReserveAmount: number,
  totalLossDate: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  const response = await CPEAPIClient.post(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
    salvageValue: recoveryAmount,
    date: totalLossDate,
  });

  return response.data.id;
};

export const updateTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  totalLossId: string,
  recoveryAmount: number,
  updatedReserveAmount: number,
  totalLossDate: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{id}'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{id}', totalLossId);

  const response = await CPEAPIClient.patch(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
    salvageValue: recoveryAmount,
    date: totalLossDate,
  });

  return response.data.id;
};

export const patchTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  updatedReserveAmount: number,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.patch(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
  });
};

export const updateClaimIsTotaLossCPE = async (
  claimId: string,
  isTotalLoss: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}'.replace('{claimId}', claimId);

  await CPEAPIClient.patch(endpoint, {
    isTotalLoss: isTotalLoss,
  });
};

export const postTotalLossComparablesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  body: Depreciation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/comparables'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);

  await CPEAPIClient.post(endpoint, {
    date: body.dateDepreciation,
    value: body.price,
    observation: body.observations,
    documentUrl: body.fileName,
  });
};

export const postTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  body: DepreciationCalculation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);

  await CPEAPIClient.post(endpoint, {
    startDate: body.PurchaseDate,
    lossDate: body.LossDate,
    newVehicleCost: body.TotalInsured,
    annualDepreciationRate: body.Percentage,
    paymentTotal: body.TotalDepreciation,
  });
};

export const updatedTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  id: string,
  body: DepreciationCalculation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations/{id}'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId)
    .replace('{id}', id);

  await CPEAPIClient.patch(endpoint, {
    startDate: body.PurchaseDate,
    lossDate: body.LossDate,
    newVehicleCost: body.TotalInsured,
    annualDepreciationRate: body.Percentage,
    paymentTotal: body.TotalDepreciation,
  });
};

export const getTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
): Promise<string | undefined> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);

  const response = await CPEAPIClient.get(endpoint);
  if (response.data.length === 0) {
    return undefined;
  }

  return response.data[0].id;
};

export type ClaimRequestCPE = {
  description: string;
  email: string;
  notify: boolean;
};

export const postClaimRequestsCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: ClaimRequestCPE,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/claimRequests'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    description: body.description,
    email: body.email,
    notify: body.notify,
  });
};

export const getSentLetters = async (
  claimId: string,
): Promise<AxiosResponse<SentLetter[]>> => {
  if (!claimId) {
    return {
      status: 400,
      statusText: 'Required claimId',
      headers: {},
      config: {} as any,
      data: [],
    } as AxiosResponse<SentLetter[]>;
  }
  const endpoint = `/v1/documents/claim/${claimId}/`;
  const response = await CPEAPIClient.get<SentLetter[]>(endpoint);

  return response;
};

export const postDownloadSentLetter = async (
  key: string,
): Promise<AxiosResponse<Blob>> => {
  const endpoint = `/v1/documents/download?key=${key}`;
  const response = await CPEAPIClient.get<Blob>(endpoint, {
    responseType: 'blob',
  });

  return response;
};

export const mappingBrandCompanyCPE: Record<any, { company: CompaniesCPE }> = {
  [BrandsId.PREMIER]: { company: CompaniesCPE.PREMIER },
  [BrandsId.OPTIMA]: { company: CompaniesCPE.OPTIMA },
  [BrandsId.MULTINATIONAL]: { company: CompaniesCPE.MULTINATIONAL },
  [BrandsId.SODA]: { company: CompaniesCPE.SODA },
};

export const getLetterTemplatesListByCompany = async (
  companyId: BrandsId,
): Promise<AxiosResponse<LetterTemplateList>> => {
  const mapCompany = mappingBrandCompanyCPE[companyId];
  if (!mapCompany) {
    console.warn('No mapping found for companyId', companyId);
    return {
      status: 404,
      statusText: 'Not Found',
      headers: {},
      config: {} as any,
      data: { templates: [], company: companyId.toString(), count: 0 },
    } as AxiosResponse<LetterTemplateList>;
  }
  const endpoint = `/v1/templates/company/${mapCompany.company}/types`;
  const response = await CPEAPIClient.get<LetterTemplateList>(endpoint);

  return response;
};

export const getLetterTemplatesList = async (): Promise<
  AxiosResponse<LetterTemplate[]>
> => {
  const endpoint = '/v1/templates/types';
  const response = await CPEAPIClient.get<LetterTemplate[]>(endpoint);

  return response;
};

export const getLetterPrefill = async (
  templateId: string,
  claimId: string,
): Promise<AxiosResponse<LetterPrefill>> => {
  const endpoint = `/v1/templates/${templateId}/prefill/${claimId}`;
  const response = await CPEAPIClient.get<LetterPrefill>(endpoint);

  return response;
};

export const postDownloadModifiedLetterTemplate = async (
  templateId: string,
  claimId: string,
  personId: string,
): Promise<AxiosResponse<Blob>> => {
  const endpoint = `/v1/templates/${templateId}/download`;
  const response = await CPEAPIClient.post<Blob>(
    endpoint,
    {
      claimId: claimId ?? '',
      personId: personId ?? '',
    },
    { responseType: 'blob' },
  );

  return response;
};

export const postSendModifiedLetterTemplate = async (
  templateId: string,
  claimId: string,
  personId: string,
): Promise<AxiosResponse<EmailSendConfirmation>> => {
  const endpoint = `/v1/templates/${templateId}/send`;
  const response = await CPEAPIClient.post<EmailSendConfirmation>(endpoint, {
    claimId: claimId ?? '',
    personId: personId ?? '',
  });

  return response;
};
